// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form } from './_rbShim';
import {
  Handle,
  Position,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { Bot, Settings, Trash2, ArrowRight, Loader2 } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import { NodesContext } from '../provider/IvrContext';
import { aiCompletionHandleId } from '../Helpers/utils';
import { listAgents, getCompletionTypes } from '@/service/aiAgentApi';
import { getUser } from '@/utilities/setting';

const NODE_COLOR = '#0284c7';
const SELECTABLE_STATUSES = new Set(['published', 'completed']);
const toCompletionTypeList = (resp: any) => {
  const list = Array.isArray(resp)
    ? resp
    : resp?.completion_types || resp?.completion_type || resp?.completions || [];
  return (Array.isArray(list) ? list : [])
    .map((item: any) =>
      typeof item === 'string'
        ? { name: item, description: '' }
        : { name: item?.name || '', description: item?.description || '' },
    )
    .filter((item: any) => item.name);
};

const AiAgentNode = ({ data, id }: any) => {
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;
  const { getEdges, deleteElements } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  const completionTypes = Array.isArray(data?.completionTypes)
    ? data.completionTypes
    : [];

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, completionTypes.length, updateNodeInternals]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [aiAgents, setAiAgents] = useState<any[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [draftAgentId, setDraftAgentId] = useState('');
  const [draftTypes, setDraftTypes] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [error, setError] = useState('');

  const agentOptions = useMemo(
    () =>
      aiAgents
        .filter(
          (a: any) =>
            a?.id !== null &&
            a?.id !== undefined &&
            a?.id !== '' &&
            a?.agent_id &&
            !a?.is_delete &&
            SELECTABLE_STATUSES.has(String(a?.status || '').toLowerCase()),
        )
        .map((a: any) => ({
          id: String(a.id),
          agentUuid: String(a.agent_id),
          name: a.name ?? a.agent_name ?? '',
        })),
    [aiAgents],
  );

  const openModal = () => {
    setDraftAgentId(data?.aiAgentId ? String(data.aiAgentId) : '');
    setDraftTypes(completionTypes);
    setError('');
    setModalIsOpen(true);
    setLoadingAgents(true);
    listAgents(getUser()?.id)
      .then((list) => setAiAgents(Array.isArray(list) ? list : []))
      .catch((err) => {
        setAiAgents([]);
        setError(typeof err === 'string' ? err : 'Failed to load AI agents');
      })
      .finally(() => setLoadingAgents(false));
  };

  const closeModal = () => {
    setError('');
    setModalIsOpen(false);
  };

  const handleAgentChange = (agentId: string) => {
    setDraftAgentId(agentId);
    setDraftTypes([]);
    setError('');
    const agent = agentOptions.find((a: any) => a.id === agentId);
    if (!agent) return;
    setLoadingTypes(true);
    getCompletionTypes(agent.agentUuid)
      .then((resp) => {
        // Ignore a late response for an agent the user already switched away from.
        setDraftAgentId((current) => {
          if (current === agentId) setDraftTypes(toCompletionTypeList(resp));
          return current;
        });
      })
      .catch((err) =>
        setError(
          typeof err === 'string' ? err : 'Failed to load completion types',
        ),
      )
      .finally(() => setLoadingTypes(false));
  };

  const handleSave = () => {
    if (!draftAgentId) {
      setError('Please select an AI agent');
      return;
    }
    if (loadingTypes) {
      setError('Completion types are still loading');
      return;
    }
    if (draftTypes.length === 0) {
      setError('The selected AI agent has no completion types');
      return;
    }
    const agent = agentOptions.find((a: any) => a.id === draftAgentId);

    // Drop edges hanging off completion types that no longer exist.
    const keep = new Set(
      draftTypes.map((t: any) => aiCompletionHandleId(id, t.name)),
    );
    const stale = getEdges().filter(
      (e: any) => e.source === id && e.sourceHandle && !keep.has(e.sourceHandle),
    );
    if (stale.length) deleteElements({ edges: stale });

    updateNodeData(id, {
      ...data,
      aiAgentId: draftAgentId,
      aiAgentUuid: agent?.agentUuid || data?.aiAgentUuid || '',
      aiAgentName: agent?.name || data?.aiAgentName || '',
      completionTypes: draftTypes,
    });
    setModalIsOpen(false);
  };

  const HEAD_H = 40;
  const DIV_H = 1;
  const AGENT_H = 30;
  const ROW_H = 26;

  return (
    <div className="ivrn" style={{ '--node-color': NODE_COLOR } as any}>
      <span
        className="ivrn-icon"
        style={{ background: NODE_COLOR, color: '#fff' }}
      >
        <Bot size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={Position.Left}
        id={id}
        style={{ background: NODE_COLOR }}
      />
      {completionTypes.map((t: any, i: number) => (
        <Handle
          key={t.name}
          type="source"
          position={Position.Right}
          id={aiCompletionHandleId(id, t.name)}
          style={{
            top: HEAD_H + DIV_H + AGENT_H + i * ROW_H + ROW_H / 2,
            backgroundColor: NODE_COLOR,
            width: 8,
            height: 8,
          }}
        />
      ))}

      <div className="ivrn-head">
        <span className="ivrn-title">AI Agent</span>
        {completionTypes.length > 0 && (
          <span
            className="ivrn-badge"
            style={{ background: '#e0f2fe', color: '#075985' }}
          >
            {completionTypes.length}
          </span>
        )}
      </div>
      <div className="ivrn-divider" />
      <div className="ivrn-body" style={{ padding: 0 }}>
        <div
          style={{
            height: AGENT_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px 0 50px',
          }}
        >
          {data?.aiAgentName ? (
            <span className="ivrn-val">{data.aiAgentName}</span>
          ) : (
            <span className="ivrn-hint">Click ⚙ to configure</span>
          )}
        </div>
        {completionTypes.map((t: any) => (
          <div
            key={t.name}
            title={t.description || t.name}
            style={{
              height: ROW_H,
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px 0 50px',
              fontSize: 12,
              fontWeight: 600,
              borderTop: '1px solid #f2f4f7',
              color: '#374151',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <ArrowRight
              size={9}
              style={{ marginRight: 5, color: NODE_COLOR, flexShrink: 0 }}
            />
            {t.name}
          </div>
        ))}
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="AI Agent"
        subtitle="Hand the call to an AI agent, then route by how the conversation completed."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Discard
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loadingTypes}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Basic Settings</div>
          <Form.Group className="mb-3">
            <Form.Label>
              AI Agent <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftAgentId}
              disabled={loadingAgents}
              isInvalid={!!error && !draftAgentId}
              onChange={(e) => handleAgentChange(e.target.value)}
            >
              <option value="">
                {loadingAgents ? 'Loading AI agents…' : 'Select AI agent'}
              </option>
              {agentOptions.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
              {/* Keep a saved agent visible even if it's since been unpublished. */}
              {draftAgentId &&
                !loadingAgents &&
                !agentOptions.some((a: any) => a.id === draftAgentId) && (
                  <option value={draftAgentId}>
                    {data?.aiAgentName || draftAgentId} (unavailable)
                  </option>
                )}
            </Form.Control>
            {!loadingAgents && agentOptions.length === 0 && (
              <Form.Text className="text-muted d-block">
                No published AI agents found.
              </Form.Text>
            )}
          </Form.Group>
        </div>

        {draftAgentId && (
          <div className="ivr-drawer-section">
            <div className="ivr-drawer-section-title">Completion Types</div>
            {loadingTypes ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
              >
                <Loader2 size={14} className="animate-spin" /> Loading completion
                types…
              </div>
            ) : draftTypes.length > 0 ? (
              <>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {draftTypes.map((t: any) => (
                    <li key={t.name} style={{ marginBottom: 4 }}>
                      <strong>{t.name}</strong>
                      {t.description ? ` — ${t.description}` : ''}
                    </li>
                  ))}
                </ul>
                {/* <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                  Each completion type gets its own output on the canvas —
                  connect every one to a next step.
                </div> */}
              </>
            ) : null}
          </div>
        )}

        {error && (
          <Form.Text className="text-danger d-block">{error}</Form.Text>
        )}
      </IvrDrawer>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default AiAgentNode;
